import {
  NavigationPanelRevealService,
  NavigationPanelService,
} from '@affine/core/modules/navigation-panel';
import {
  EMPTY_NAVIGATION_ARGS,
  navigationActionId,
  NavigationActivationError,
  type UiNavigationDefinition,
} from '@affine/core/modules/streamlain/copilot/workspace-navigation-catalog';
import { WorkspaceViewContextService } from '@affine/core/modules/streamlain/copilot/workspace-view-context';
import { WorkbenchService } from '@affine/core/modules/workbench';
import {
  FolderIcon,
  PageIcon,
  TagIcon,
  ViewLayersIcon,
} from '@blocksuite/icons/rc';
import { LiveData, useLiveData, useService } from '@toeverything/infra';
import {
  createContext,
  type PropsWithChildren,
  type ReactNode,
  type RefObject,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

export type TreeNavigationKind = 'doc' | 'folder' | 'tag' | 'collection';
const TREE_NAVIGATION_ICONS = {
  doc: <PageIcon />,
  folder: <FolderIcon />,
  tag: <TagIcon />,
  collection: <ViewLayersIcon />,
} satisfies Record<TreeNavigationKind, ReactNode>;
export interface TreeNavigationRoot {
  kind: TreeNavigationKind;
  id: string;
  label: string;
}
export interface TreeNavigationEntry extends TreeNavigationRoot {
  parentPath: string[];
}
export interface TreeNavigationRow {
  id: string;
  type: string;
  data: string;
  parentId?: string | null;
  trashed?: boolean | null;
}
export interface TreeNavigationDoc {
  id: string;
  title: string;
  trashed: boolean;
  parentTrashed?: boolean;
}
export const treeNavigationOwnerId = (path: readonly string[]) =>
  JSON.stringify(['tree', ...path]);
export const treeNavigationEntryId = (kind: TreeNavigationKind, id: string) =>
  JSON.stringify([kind, id]);
export const TREE_SECTION_ID = 'section';

/** A transient projection of the ordinary, actor-filtered tree metadata. Never loads document content. */
export function projectNavigationTree(
  roots: readonly TreeNavigationRoot[],
  rows: readonly TreeNavigationRow[],
  docs: readonly TreeNavigationDoc[],
  path: string[],
  hiddenDocIds: ReadonlySet<string> = new Set()
): TreeNavigationEntry[] {
  const children = new Map<string, TreeNavigationRow[]>();
  for (const row of rows) {
    if (!row.parentId) continue;
    const list = children.get(row.parentId) ?? [];
    list.push(row);
    children.set(row.parentId, list);
  }
  const docsById = new Map(docs.map(doc => [doc.id, doc]));
  const rowsById = new Map(rows.map(row => [row.id, row]));
  const seen = new Set<string>(),
    result: TreeNavigationEntry[] = [];
  const visit = (root: TreeNavigationRoot, parentPath: string[]) => {
    const key = treeNavigationEntryId(root.kind, root.id);
    if (seen.has(key) || rowsById.get(root.id)?.trashed) return;
    if (root.kind === 'folder' && !rowsById.has(root.id)) return;
    if (root.kind === 'doc') {
      const doc = docsById.get(root.id);
      if (!doc || doc.trashed || doc.parentTrashed || hiddenDocIds.has(root.id))
        return;
      root = { ...root, label: doc.title };
    }
    seen.add(key);
    result.push({ ...root, parentPath });
    if (root.kind !== 'folder') return;
    const nextPath = [...parentPath, `folder-${root.id}`];
    for (const row of children.get(root.id) ?? []) {
      if (
        row.trashed ||
        !['doc', 'folder', 'tag', 'collection'].includes(row.type)
      )
        continue;
      visit(
        {
          kind: row.type as TreeNavigationKind,
          id: row.type === 'folder' ? row.id : row.data,
          label: row.data,
        },
        nextPath
      );
    }
  };
  for (const root of roots) visit(root, path);
  return result;
}

interface TreeNavigationValue {
  context: WorkspaceViewContextService;
  scope: { workspaceId: string; viewId: string };
  ownerId: string;
  entries: readonly TreeNavigationEntry[];
}
const TreeNavigationContext = createContext<TreeNavigationValue | null>(null);

function bindTreeElement(
  value: Pick<TreeNavigationValue, 'context' | 'scope' | 'ownerId'>,
  definitionId: string,
  root: HTMLElement,
  element: HTMLElement
) {
  const actionId = navigationActionId(value.scope, value.ownerId, definitionId);
  element.dataset.uiNavigationId = actionId;
  const dispose = value.context.navigation.bind(
    value.scope,
    value.ownerId,
    definitionId,
    root,
    element,
    element,
    (args, click) => {
      if (Object.keys(args).length || !root.isConnected || !element.isConnected)
        throw new NavigationActivationError('blocked');
      const resolved = value.context.navigation.resolveParent(
        value.scope,
        actionId
      );
      if (!resolved.ok) throw new NavigationActivationError(resolved.reason);
      if (resolved.binding?.element !== element)
        throw new NavigationActivationError('blocked');
      const snapshot = value.context.snapshot(value.scope);
      if (!snapshot.ok)
        throw new NavigationActivationError(
          snapshot.reason === 'stale' ? 'stale' : 'blocked'
        );
      click();
    }
  );
  return () => {
    dispose();
    if (element.dataset.uiNavigationId === actionId)
      delete element.dataset.uiNavigationId;
  };
}

/** Bind a real normal-owner element without replacing selection, modifier, DnD or contextmenu handlers. */
export function useTreeNavigationBinding(
  ref: RefObject<HTMLElement | null>,
  object: Pick<TreeNavigationRoot, 'kind' | 'id'> | undefined,
  selector?: string,
  revisionState?: boolean
) {
  const value = useContext(TreeNavigationContext);
  const kind = object?.kind,
    id = object?.id;
  useLayoutEffect(() => {
    if (
      !value ||
      !kind ||
      !id ||
      !value.entries.some(entry => entry.kind === kind && entry.id === id)
    )
      return;
    const root = ref.current;
    const element = selector
      ? root?.querySelector<HTMLElement>(selector)
      : root;
    if (!element || !root) return;
    const definitionId = treeNavigationEntryId(kind, id);
    return bindTreeElement(value, definitionId, root, element);
  }, [id, kind, ref, selector, value]);
  useLayoutEffect(() => {
    if (value && kind && id) value.context.invalidate(value.scope, 'workbench');
  }, [id, kind, revisionState, value]);
}

export function NavigationTreeSectionOwner({
  path,
  label,
  entries,
  headerRef,
  headerSelector,
  expanded,
  onExpand,
  children,
}: PropsWithChildren<{
  path: string[];
  label: string;
  entries: readonly TreeNavigationEntry[];
  headerRef: RefObject<HTMLElement | null>;
  headerSelector?: string;
  expanded?: boolean;
  onExpand: () => void;
}>) {
  const parent = useContext(TreeNavigationContext);
  const context = useService(WorkspaceViewContextService);
  const workbench = useService(WorkbenchService).workbench;
  const panel = useService(NavigationPanelService),
    reveal = useService(NavigationPanelRevealService);
  const view = useLiveData(workbench.activeView$);
  const scope = useMemo(
    () => ({ workspaceId: context.workspaceId, viewId: view.id }),
    [context, view.id]
  );
  const ownerId = treeNavigationOwnerId(path);
  const items$ = useMemo(
    () => new LiveData<readonly UiNavigationDefinition[]>([]),
    []
  );
  const entriesRef = useRef(entries);
  const disclosureRef = useRef({ expanded, onExpand });
  useLayoutEffect(() => {
    entriesRef.current = entries;
    disclosureRef.current = { expanded, onExpand };
  }, [entries, expanded, onExpand]);
  const collapsed = useLiveData(panel.collapsed$(path));
  useLayoutEffect(() => {
    context.invalidate(scope, 'workbench');
  }, [collapsed, context, expanded, scope]);
  const value = useMemo(
    () => ({ context, scope, ownerId, entries }),
    [context, entries, ownerId, scope]
  );
  const parentActionId = parent
    ? navigationActionId(parent.scope, parent.ownerId, TREE_SECTION_ID)
    : null;
  const definitions = useMemo(() => {
    const current = () => {
      const state = context.snapshot(scope);
      if (!state.ok)
        throw new NavigationActivationError(
          state.reason === 'stale' ? 'stale' : 'blocked'
        );
      return state.snapshot;
    };
    const target = (entryId: string) => {
      const snapshot = context.snapshot(scope);
      if (!snapshot.ok) return null;
      const state = snapshot.snapshot;
      return {
        ...scope,
        revision: state.revision,
        surfaceId: state.surfaceId,
        route: state.route,
        title: state.title,
        sidebar: { side: 'left' as const, entryId },
      };
    };
    const section: UiNavigationDefinition = {
      id: TREE_SECTION_ID,
      label,
      icon: TREE_NAVIGATION_ICONS.folder,
      keywords: [],
      parentIds: parentActionId ? [parentActionId] : [],
      argsSchema: EMPTY_NAVIGATION_ARGS,
      available: () => true,
      activate: () => {
        current();
        disclosureRef.current.onExpand();
      },
      readTarget: () =>
        headerRef.current &&
        (disclosureRef.current.expanded ?? !panel.collapsed$(path).value)
          ? target(TREE_SECTION_ID)
          : null,
    };
    return [
      section,
      ...entries.map((entry): UiNavigationDefinition => {
        const id = treeNavigationEntryId(entry.kind, entry.id),
          folderPath = [...entry.parentPath, `folder-${entry.id}`];
        return {
          id,
          label: entry.label,
          icon: TREE_NAVIGATION_ICONS[entry.kind],
          section: label,
          docId: entry.kind === 'doc' ? entry.id : undefined,
          keywords: [],
          argsSchema: EMPTY_NAVIGATION_ARGS,
          parentIds: [
            navigationActionId(scope, ownerId, TREE_SECTION_ID),
            ...entry.parentPath
              .slice(path.length)
              .map(segment =>
                navigationActionId(
                  scope,
                  ownerId,
                  treeNavigationEntryId(
                    'folder',
                    segment.slice('folder-'.length)
                  )
                )
              ),
          ],
          available: () =>
            entriesRef.current.some(
              item => item.kind === entry.kind && item.id === entry.id
            ),
          activate: () => {
            current();
            disclosureRef.current.onExpand();
            if (entry.kind === 'folder') {
              panel.setCollapsed(folderPath, false);
              reveal.revealFolder(entry.id, { workspaceId: scope.workspaceId });
            }
            if (entry.kind === 'doc') {
              workbench.openDoc(entry.id);
              reveal.revealDoc(entry.id, { workspaceId: scope.workspaceId });
            }
            if (entry.kind === 'tag') workbench.openTag(entry.id);
            if (entry.kind === 'collection') workbench.openCollection(entry.id);
          },
          readTarget: () => {
            if (entry.kind === 'folder')
              return panel.collapsed$(folderPath).value ? null : target(id);
            const pathname =
              entry.kind === 'doc'
                ? `/${entry.id}`
                : entry.kind === 'tag'
                  ? `/tag/${entry.id}`
                  : `/collection/${entry.id}`;
            return workbench.location$.value.pathname === pathname
              ? target(id)
              : null;
          },
        };
      }),
    ];
  }, [
    context,
    entries,
    headerRef,
    label,
    ownerId,
    panel,
    parentActionId,
    path,
    reveal,
    scope,
    workbench,
  ]);
  useLayoutEffect(
    () => context.navigation.registerSource({ scope, ownerId, items$ }),
    [context, items$, ownerId, scope]
  );
  useLayoutEffect(() => items$.next(definitions), [definitions, items$]);
  useLayoutEffect(() => {
    const root = headerRef.current;
    const element = headerSelector
      ? root?.querySelector<HTMLElement>(headerSelector)
      : root;
    if (!element) return;
    return bindTreeElement(
      { context, scope, ownerId },
      TREE_SECTION_ID,
      root!,
      element
    );
  }, [context, headerRef, headerSelector, ownerId, scope]);
  return (
    <TreeNavigationContext.Provider value={value}>
      {children}
    </TreeNavigationContext.Provider>
  );
}
